import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {useDismissible} from "@khan/component-dismissible";
import {useGuideAudioController} from "@khan/guide-core";

import {TextToSpeechIntroModal} from "./text-to-speech-intro-modal.tsx";
import {TextToSpeechOnboardingContext} from "./text-to-speech-onboarding-context.ts";
import {TextToSpeechVoiceSelectionModal} from "./text-to-speech-voice-selection-modal.tsx";

interface OnboardingStep {
    style?: StyleType;
    onClose: () => void;
    onNext: () => void;
}

const onboardingSteps: Array<React.FC<OnboardingStep>> = [
    TextToSpeechIntroModal,
    TextToSpeechVoiceSelectionModal,
];

interface Props {
    children: React.ReactNode;
}

/**
 * Launches the Guide text-to-speech onboarding modal.
 */
export const TextToSpeechOnboardingProvider = (
    props: Props,
): React.ReactElement => {
    const {children} = props;
    const [isShowing, setIsShowing] = useState(false);
    const [step, setStep] = useState(0);
    const {
        isDismissedResult: isOnboardingDismissedResult,
        dismiss: dismissOnboardingForNDays,
    } = useDismissible("text-to-speech-onboarding");
    const {stop} = useGuideAudioController();

    const show = useCallback(() => setIsShowing(true), []);

    const handleClose = useCallback(() => {
        setIsShowing(false);
        dismissOnboardingForNDays(
            // "never" is a sentinel value that means "dismiss forever".
            // We can still prompt the onboarding experience again
            // programmatically using the show() function.
            "never",
        );
        stop();
        setStep(0);
    }, [dismissOnboardingForNDays, stop]);

    const handleNext = useCallback(() => {
        setStep((currentStep) => (currentStep + 1) % onboardingSteps.length);
    }, []);

    const context = useMemo(() => {
        return {
            initiateOnboarding: show,
            hasOnboardingBeenViewed:
                // If we fail to load the dismissible result, we treat it as
                // already seen to be conservative. Otherwise, return the
                // result of the dismissible.
                isOnboardingDismissedResult.status !== "success" ||
                (isOnboardingDismissedResult.status === "success" &&
                    isOnboardingDismissedResult.data),
        };
    }, [isOnboardingDismissedResult, show]);

    const CurrentStep = onboardingSteps[step];

    return (
        <TextToSpeechOnboardingContext.Provider value={context}>
            <ModalLauncher
                backdropDismissEnabled={false}
                modal={({closeModal}) => (
                    <CurrentStep
                        style={styles.modal}
                        onClose={closeModal}
                        onNext={handleNext}
                    />
                )}
                opened={isShowing}
                onClose={handleClose}
            />
            {children}
        </TextToSpeechOnboardingContext.Provider>
    );
};

const styles = StyleSheet.create({
    modal: {
        height: "fit-content",
        maxHeight: "100%",
        width: "100%",
        maxWidth: 288, // as designed
    },
});
